import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

// 一覧画面の共通フレーム。
// 要件（社長指示）：表ヘッダは固定・データ部だけスクロール・件数/ページャは下部に常時表示。
// サーバ側 検索/ソート/ページングの状態管理（localStorage 永続化）もここに集約する。

export type SortDir = 'asc' | 'desc';

export interface ListControls<S extends string> {
  q: string;
  sortBy: S;
  sortDir: SortDir;
  page: number;
  pageSize: number;
}

// 検索条件を localStorage に保存し、次回オープン時に復元する（要件: 条件の永続化）。
export function useListControls<S extends string>(storageKey: string, defaults: ListControls<S>) {
  const [state, setState] = useState<ListControls<S>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return { ...defaults, ...(JSON.parse(raw) as Partial<ListControls<S>>) };
    } catch {
      /* 壊れていれば既定で開始 */
    }
    return defaults;
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      /* localStorage 不可環境は無視 */
    }
  }, [storageKey, state]);

  const set = (patch: Partial<ListControls<S>>) => setState((s) => ({ ...s, ...patch }));
  return {
    ...state,
    // q・並び替え・件数の変更時は 1 ページ目に戻す（件数ズレ防止）。
    setQ: (q: string) => set({ q, page: 1 }),
    setSortBy: (sortBy: S) => set({ sortBy, page: 1 }),
    setSortDir: (sortDir: SortDir) => set({ sortDir, page: 1 }),
    setPage: (page: number) => set({ page }),
    setPageSize: (pageSize: number) => set({ pageSize, page: 1 }),
    // 列ヘッダ（タイトル行）クリックでの並び替え（お手本: エージェント一覧）。
    // 同じ列を再クリックで昇順⇄降順をトグル。別の列なら昇順から。
    toggleSort: (field: S) =>
      setState((s) =>
        s.sortBy === field
          ? { ...s, sortDir: s.sortDir === 'asc' ? 'desc' : 'asc', page: 1 }
          : { ...s, sortBy: field, sortDir: 'asc', page: 1 },
      ),
  };
}

// 列ヘッダ（タイトル行）クリックで並び替えする th。テーブル系の thead で使う。
// 矢印(▲▼)で現在のソート列と向きを示す。div ベースのヘッダは sortArrow を直接使う。
export function SortHeader<S extends string>({
  field,
  controls,
  children,
  style,
}: {
  field: S;
  controls: ReturnType<typeof useListControls<S>>;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <th
      style={{ ...stickyTh, cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => controls.toggleSort(field)}
      title="クリックで並び替え"
    >
      {children}
      <span style={{ color: '#2563eb' }}>{sortArrow(controls, field)}</span>
    </th>
  );
}

// 現在のソート状態を表す矢印（その列でなければ空文字）。div ヘッダ等で流用する。
export function sortArrow<S extends string>(
  controls: { sortBy: S; sortDir: SortDir },
  field: S,
): string {
  if (controls.sortBy !== field) return '';
  return controls.sortDir === 'asc' ? ' ▲' : ' ▼';
}

// 固定ヘッダ用の th スタイル（スクロール時に動かない）。各ページの thead th に展開して使う。
export const stickyTh: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: '#f5f7fa',
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 12,
  fontWeight: 700,
  color: '#64748b',
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap',
};

const PAGE_SIZES = [10, 20, 50, 100];

export function DataListFrame<S extends string>({
  title,
  total,
  loading,
  controls,
  searchPlaceholder = '検索…（名前・説明）',
  filters,
  rightActions,
  bodyMaxHeight = 'calc(100vh - 240px)',
  children,
}: {
  title: string;
  total: number;
  loading?: boolean;
  controls: ReturnType<typeof useListControls<S>>;
  // 並び替えは列ヘッダ（タイトル行）クリックで行う（SortHeader / sortArrow）。
  // 旧ドロップダウン式は廃止（社長指示: ヘッダクリックが直感的）。後方互換で任意指定は残す。
  sortOptions?: { value: S; label: string }[];
  searchPlaceholder?: string;
  // マスタ的な項目の絞り込み（区分/OS/有効無効 等）をドロップダウンで置くスロット。
  // 任意文字列は上の検索入力（あいまい検索）、選択肢の決まった項目はここ（要件）。
  filters?: ReactNode;
  rightActions?: ReactNode;
  bodyMaxHeight?: string;
  children: ReactNode;
}) {
  const totalPages = Math.max(1, Math.ceil(total / controls.pageSize));
  const page = Math.min(controls.page, totalPages);
  const from = total === 0 ? 0 : (page - 1) * controls.pageSize + 1;
  const to = Math.min(page * controls.pageSize, total);

  return (
    <section
      className="k-card"
      style={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* ツールバー：タイトル＋件数・検索・並び替え・右アクション（＋新規 等） */}
      <div
        style={{
          padding: '14px 18px',
          borderBottom: '1px solid #eef2f7',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>
          {title}
          <span className="k-muted" style={{ fontWeight: 400, fontSize: 14, marginLeft: 6 }}>
            （{total}件）
          </span>
        </h2>
        <input
          value={controls.q}
          onChange={(e) => controls.setQ(e.target.value)}
          placeholder={searchPlaceholder}
          style={{ flex: '1 1 220px', maxWidth: 360, padding: '7px 12px', fontSize: 14 }}
        />
        {/* 並び替えは列ヘッダ（タイトル行）クリックで行う（▲▼）。ツールバーのドロップダウンは廃止。 */}
        {/* マスタ的な項目の絞り込み（選択式）。任意文字列は上の検索（あいまい）と役割分担。 */}
        {filters && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{filters}</div>}
        {rightActions && <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>{rightActions}</div>}
      </div>

      {/* データ部：ここだけスクロール。子（table）の thead に stickyTh を当てるとヘッダ固定。 */}
      <div style={{ overflow: 'auto', maxHeight: bodyMaxHeight, minHeight: 140 }}>
        {loading ? (
          <p style={{ padding: 18 }}>Loading…</p>
        ) : total === 0 ? (
          <p className="k-muted" style={{ padding: 18 }}>
            該当するデータがありません。
          </p>
        ) : (
          children
        )}
      </div>

      {/* フッタ：常時表示の件数＋ページャ */}
      <div
        style={{
          padding: '10px 18px',
          borderTop: '1px solid #eef2f7',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
          background: '#fbfcfe',
        }}
      >
        <span className="k-muted" style={{ fontSize: 13 }}>
          {from}–{to} / {total}件
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="k-muted" style={{ fontSize: 12 }}>
            表示件数
          </span>
          <select
            value={controls.pageSize}
            onChange={(e) => controls.setPageSize(Number(e.target.value))}
            style={{ padding: '6px 8px', fontSize: 13 }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className="btn-ghost"
            type="button"
            disabled={page <= 1}
            onClick={() => controls.setPage(1)}
            style={{ padding: '6px 10px' }}
          >
            «
          </button>
          <button
            className="btn-ghost"
            type="button"
            disabled={page <= 1}
            onClick={() => controls.setPage(page - 1)}
            style={{ padding: '6px 10px' }}
          >
            ‹ 前
          </button>
          <span style={{ fontSize: 13, minWidth: 92, textAlign: 'center' }}>
            {page} / {totalPages} ページ
          </span>
          <button
            className="btn-ghost"
            type="button"
            disabled={page >= totalPages}
            onClick={() => controls.setPage(page + 1)}
            style={{ padding: '6px 10px' }}
          >
            次 ›
          </button>
          <button
            className="btn-ghost"
            type="button"
            disabled={page >= totalPages}
            onClick={() => controls.setPage(totalPages)}
            style={{ padding: '6px 10px' }}
          >
            »
          </button>
        </div>
      </div>
    </section>
  );
}
